"use client";
import { useEffect, useRef, useState } from "react";
import {
  usePublicReferralInvite,
  usePublicRevenueImpact,
  trackReferralClick,
} from "../services/referralClient";
import { getStoredReferralAttribution } from "../services/referralStorage";
import { useReferralAnalytics } from "./useReferralAnalytics";
import { buildAppLoginUrl, getPortalBaseUrl } from "@/lib/utils";
import {
  REFERRAL_ATTRIBUTION_DISABLED_VALUE,
  REFERRAL_ATTRIBUTION_QUERY_PARAM,
} from "../services/referralUrl";

type ReferralInviteTrackingOptions = {
  disableReferralAttribution?: boolean;
};

export function useReferralInviteTracking(
  creatorUniqueId?: string,
  options: ReferralInviteTrackingOptions = {},
) {
  const disableReferralAttribution =
    options.disableReferralAttribution === true;
  const trackedRef = useRef(false);
  const [isStartingSignup, setIsStartingSignup] = useState(false);
  const { data, isLoading, isError } = usePublicReferralInvite(creatorUniqueId || "");
  const { data: impact } = usePublicRevenueImpact(data?.id);
  const { trackReferAndEarnInvitation } = useReferralAnalytics();

  useEffect(() => {
    if (!creatorUniqueId || !isError) return;
    window.location.href = getPortalBaseUrl();
  }, [creatorUniqueId, isError]);

  useEffect(() => {
    if (!creatorUniqueId || disableReferralAttribution || trackedRef.current) return;
    trackedRef.current = true;
    trackReferralClick(creatorUniqueId).catch(() => {
      // Signup must keep working even when attribution tracking fails.
    });
  }, [creatorUniqueId, disableReferralAttribution]);

  const handleSignupClick = async () => {
    if (isStartingSignup) return;

    setIsStartingSignup(true);
    if (!creatorUniqueId || disableReferralAttribution) {
      window.location.href = buildAppLoginUrl({
        [REFERRAL_ATTRIBUTION_QUERY_PARAM]: REFERRAL_ATTRIBUTION_DISABLED_VALUE,
      });
      return;
    }

    trackReferAndEarnInvitation(creatorUniqueId);
    try {
      let stored = getStoredReferralAttribution();
      if (!stored || stored.creatorUniqueId !== creatorUniqueId) {
        const click = await trackReferralClick(creatorUniqueId);
        stored = {
          attributionToken: click.attributionToken,
          creatorUniqueId: click.creatorUniqueId,
          expiresAt: click.expiresAt,
        };
      }

      const loginUrl = buildAppLoginUrl({
        referralToken: stored?.attributionToken,
      });
      window.location.href = loginUrl;
    } catch {
      window.location.href = buildAppLoginUrl();
    }
  };

  return {
    data,
    impact,
    isLoading,
    isError,
    isStartingSignup,
    handleSignupClick,
  };
}
